import type { SingleAssetValueOverview } from '@einwert/valuation-api-client'
import { Divider, Stack } from '@mui/material'
import { Button, LoadingSkeleton, tokens, TOOLBAR_HEIGHT } from 'ComponentLibrary'

import { MarketDataSidebarSelection } from 'ClientPortal/src/pages/MarketData/Sidebar/MarketDataSidebarSelection'
import { MarketDataSidebarOption } from 'ClientPortal/src/pages/MarketData/Sidebar/MarketDataSidebarOption'
import { MarketDataSidebarSearch } from 'ClientPortal/src/pages/MarketData/Sidebar/MarketDataSidebarSearch'
import { useState } from 'react'
import { AssetLocation } from 'ClientPortal/src/pages/MarketData/types'
import { MapPinIcon } from '@phosphor-icons/react'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'

type MarketDataSidebarProps = {
    assets: SingleAssetValueOverview[]
    isAssetsPending?: boolean
    selectedLocation?: AssetLocation
    setSelectedLocation: (location?: AssetLocation) => void
}

export const MARKET_DATA_SIDEBAR_WIDTH = '327px'

export const MarketDataSidebar = ({ assets, selectedLocation, setSelectedLocation, isAssetsPending }: MarketDataSidebarProps) => {
    const { t } = useTranslation('pages', { keyPrefix: 'marketData.sidebar' })
    const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false)
    const { isMarketDataEnabled, isMarketDataFromValuationEnabled } = useFlags()

    const shouldHaveMarketData = (asset: SingleAssetValueOverview) =>
        isMarketDataEnabled || asset.valuation_log.some((valuation) => valuation.valuation_type !== 'historical')

    const assetsWithMarketData = assets.filter(shouldHaveMarketData)

    return (
        <Stack position="relative">
            <Stack
                direction="column"
                width={MARKET_DATA_SIDEBAR_WIDTH}
                minWidth={MARKET_DATA_SIDEBAR_WIDTH}
                maxHeight={`calc(100vh - ${TOOLBAR_HEIGHT} - 1px)`}
                position="sticky"
                top={0}
            >
                <Stack padding={tokens.SizingSpacing300} gap={tokens.SizingSpacing300}>
                    <Typography variant="headline2" color={tokens.ColorTextBoldest}>
                        {t('title')}
                    </Typography>
                    {selectedLocation && (
                        <MarketDataSidebarSelection selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} />
                    )}
                </Stack>
                <Divider />
                <Stack direction="column" maxHeight="100%" overflow="auto" gap={tokens.SizingSpacing300} padding={tokens.SizingSpacing300}>
                    {isMarketDataEnabled && !isMarketDataFromValuationEnabled && (
                        <>
                            <Button
                                variant="secondary"
                                startIcon={<MapPinIcon size={tokens.SizingIconM} />}
                                label={t('customLocation')}
                                onClick={() => setIsAddressSearchOpen(true)}
                            />
                            <Divider />
                        </>
                    )}

                    {isAssetsPending ? (
                        <LoadingSkeleton count={4} height="110px" sx={{ transform: 'none' }} />
                    ) : !isEmpty(assetsWithMarketData) ? (
                        assetsWithMarketData
                            .filter((asset) => asset.uuid !== selectedLocation?.uuid)
                            .map((asset) => (
                                <MarketDataSidebarOption key={asset.uuid} asset={asset} setSelectedLocation={setSelectedLocation} />
                            ))
                    ) : (
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {t('emptyText')}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            <MarketDataSidebarSearch
                setSelectedLocation={setSelectedLocation}
                isOpen={isAddressSearchOpen}
                setIsOpen={setIsAddressSearchOpen}
            />
        </Stack>
    )
}
